/**
 * Centralized Navigation Configuration
 *
 * This file defines navigation structures for all user types.
 * Navigation items are organized into sections with role/permission-based visibility.
 *
 * User Types:
 * - learner: All items visible to all learners (no permission checks)
 * - employer: Items filtered by is_primary_representative (adminOnly flag)
 * - platform: Items filtered by RBAC permissions (permission flag)
 */

import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  BookOpen,
  TrendingUp,
  FileText,
  Users,
  User,
  Home,
  Building2,
  BarChart3,
  Network,
  Shield,
  Mail,
  BarChart,
  StickyNote,
  Bookmark,
  GraduationCap,
  Target,
  Library,
  Settings,
  Star,
  BookMarked,
  Map,
  Download,
} from 'lucide-react';
import { ROUTES } from '@/config/routes';
import type { PermissionName } from '@/types/rbac';

/**
 * Navigation item structure
 */
export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  ariaLabel: string;
  adminOnly?: boolean; // For employer admin-only items
  permission?: PermissionName | null; // For platform permission-based items
  children?: NavItem[]; // For collapsible submenu items
}

/**
 * Navigation section structure
 */
export interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * Navigation configuration structure
 */
export interface NavigationConfig {
  dashboard: NavItem;
  sections: NavSection[];
}

/**
 * Learner Navigation Configuration
 * Total: 14 items across 5 collapsible parent sections in 5 section headings
 * No permission checks - all items visible to all learners
 *
 * Section Headings and Items:
 * - Learning: Learning Hub (Workshops, Resource Library, Wellness Tools)
 * - Progress: My Progress (Progress Tracker, Skills, Learning Analytics)
 * - Portfolio: My Portfolio (CVs, Cover Letters, Notes, Bookmarks, Email Templates, Career Planner, Interview Prep)
 * - Network: My Network (Company Directory)
 * - Settings: My Settings (Profile)
 *
 * Parent sections are clickable and navigate to their parent route.
 */
export const LEARNER_NAV: NavigationConfig = {
  dashboard: {
    label: 'Dashboard',
    to: ROUTES.LEARNER_DASHBOARD,
    icon: LayoutDashboard,
    ariaLabel: 'Dashboard - Your learning overview',
  },
  sections: [
    {
      title: 'Learning',
      items: [
        {
          label: 'Learning Hub',
          to: ROUTES.LEARNER_LEARNING_HUB,
          icon: BookOpen,
          ariaLabel: 'Learning Hub - Your learning centre',
          children: [
            {
              label: 'Workshops',
              to: ROUTES.LEARNER_LEARNING_HUB_WORKSHOPS,
              icon: GraduationCap,
              ariaLabel: 'Workshops - Access workshop catalogue',
            },
            {
              label: 'Resource Library',
              to: ROUTES.LEARNER_LEARNING_HUB_RESOURCE_LIBRARY,
              icon: Library,
              ariaLabel: 'Resource Library - Helpful guides and templates',
            },
          ],
        },
      ],
    },
    {
      title: 'Progress',
      items: [
        {
          label: 'My Progress',
          to: ROUTES.LEARNER_PROGRESS,
          icon: TrendingUp,
          ariaLabel: 'My Progress - Track your learning journey',
          children: [
            {
              label: 'Progress Tracker',
              to: ROUTES.LEARNER_PROGRESS,
              icon: TrendingUp,
              ariaLabel: 'Progress Tracker - Track your learning journey',
            },
            {
              label: 'Skills',
              to: ROUTES.LEARNER_SKILLS,
              icon: Target,
              ariaLabel: 'Skills - View and assess your skills',
            },
            {
              label: 'Learning Analytics',
              to: ROUTES.LEARNER_ANALYTICS,
              icon: BarChart,
              ariaLabel: 'Learning Analytics - View detailed learning statistics',
            },
          ],
        },
      ],
    },
    {
      title: 'Portfolio',
      items: [
        {
          label: 'My Portfolio',
          to: ROUTES.LEARNER_PORTFOLIO,
          icon: FileText,
          ariaLabel: 'My Portfolio - Your career documents',
          children: [
            {
              label: 'CVs',
              to: ROUTES.LEARNER_CV_LIST,
              icon: FileText,
              ariaLabel: 'CVs - View and manage your CVs',
            },
            {
              label: 'Cover Letters',
              to: ROUTES.LEARNER_COVER_LETTERS_LIST,
              icon: Mail,
              ariaLabel: 'Cover Letters - View and manage cover letters',
            },
            {
              label: 'Notes',
              to: ROUTES.LEARNER_NOTES_LIST,
              icon: StickyNote,
              ariaLabel: 'Notes - View and manage your notes',
            },
            {
              label: 'Bookmarks',
              to: ROUTES.LEARNER_BOOKMARKS,
              icon: Bookmark,
              ariaLabel: 'Bookmarks - View your saved items',
            },
            {
              label: 'Email Templates',
              to: ROUTES.LEARNER_EMAIL_TEMPLATES_LIST,
              icon: Mail,
              ariaLabel: 'Email Templates - View and manage email templates',
            },
            {
              label: 'Career Planner',
              to: ROUTES.LEARNER_CAREER_PLANNER,
              icon: Target,
              ariaLabel: 'Career Planner - Plan your career goals',
            },
            {
              label: 'Interview Prep',
              to: ROUTES.LEARNER_STAR_EXAMPLES,
              icon: Star,
              ariaLabel: 'Interview Prep - STAR examples for interviews',
            },
          ],
        },
      ],
    },
    {
      title: 'Network',
      items: [
        {
          label: 'My Network',
          to: ROUTES.LEARNER_COMPANIES,
          icon: Network,
          ariaLabel: 'My Network - Professional connections',
          children: [
            {
              label: 'Company Directory',
              to: ROUTES.LEARNER_COMPANIES,
              icon: Building2,
              ariaLabel: 'Company Directory - Browse employer profiles',
            },
          ],
        },
      ],
    },
    {
      title: 'Settings',
      items: [
        {
          label: 'My Settings',
          to: ROUTES.SETTINGS_PROFILE,
          icon: Settings,
          ariaLabel: 'My Settings - Your account settings',
          children: [
            {
              label: 'Profile',
              to: ROUTES.SETTINGS_PROFILE,
              icon: User,
              ariaLabel: 'Profile - Your account settings',
            },
          ],
        },
      ],
    },
  ],
};

/**
 * Employer Navigation Configuration
 * Total: 6 items (1 dashboard + 5 section items)
 * Analytics is admin-only (requires is_primary_representative = true)
 */
export const EMPLOYER_NAV: NavigationConfig = {
  dashboard: {
    label: 'Dashboard',
    to: ROUTES.EMPLOYER_DASHBOARD,
    icon: Home,
    ariaLabel: 'Dashboard - Your employer overview',
  },
  sections: [
    {
      title: 'Company',
      items: [
        {
          label: 'Company Profile',
          to: ROUTES.EMPLOYER_COMPANY,
          icon: Building2,
          ariaLabel: 'Company Profile - Manage your organisation details',
        },
        {
          label: 'Team',
          to: ROUTES.EMPLOYER_TEAM,
          icon: Users,
          ariaLabel: 'Team Management - Manage team members and invites',
        },
      ],
    },
    {
      title: 'Insights',
      items: [
        {
          label: 'Analytics',
          to: ROUTES.EMPLOYER_ANALYTICS,
          icon: BarChart3,
          ariaLabel: 'Analytics - View engagement metrics',
          adminOnly: true,
        },
      ],
    },
    {
      title: 'Network',
      items: [
        {
          label: 'Network',
          to: ROUTES.NETWORK_COMPANIES,
          icon: Network,
          ariaLabel: 'Network - Professional connections',
        },
      ],
    },
    {
      title: 'Settings',
      items: [
        {
          label: 'Settings',
          to: ROUTES.SETTINGS_PROFILE,
          icon: User,
          ariaLabel: 'Settings - Your account settings',
        },
      ],
    },
  ],
};

/**
 * Platform Navigation Configuration
 * Total: 9 items (1 dashboard + 8 section items)
 * All items have permission requirements except Profile
 */
export const PLATFORM_NAV: NavigationConfig = {
  dashboard: {
    label: 'Dashboard',
    to: ROUTES.ADMIN_DASHBOARD,
    icon: LayoutDashboard,
    ariaLabel: 'Dashboard - Platform overview',
  },
  sections: [
    {
      title: 'System Management',
      items: [
        {
          label: 'Users',
          to: ROUTES.ADMIN_USERS,
          icon: Users,
          ariaLabel: 'Users - Manage platform users',
          permission: 'user:manage',
        },
        {
          label: 'Roles',
          to: ROUTES.ADMIN_ROLES,
          icon: Shield,
          ariaLabel: 'Roles - Manage roles and permissions',
          permission: 'admin:manage',
        },
        {
          label: 'Email',
          to: ROUTES.ADMIN_EMAIL,
          icon: Mail,
          ariaLabel: 'Email - Manage email templates',
          permission: 'content:edit',
        },
      ],
    },
    {
      title: 'Content Management',
      items: [
        {
          label: 'Workshops',
          to: ROUTES.ADMIN_WORKSHOPS,
          icon: BookOpen,
          ariaLabel: 'Workshops - Manage workshop content',
          permission: 'workshop:manage',
        },
        {
          label: 'Content',
          to: ROUTES.ADMIN_CONTENT,
          icon: Library,
          ariaLabel: 'Content - Manage platform content',
          permission: 'content:view',
        },
      ],
    },
    {
      title: 'Network Management',
      items: [
        {
          label: 'Companies',
          to: ROUTES.ADMIN_COMPANIES,
          icon: Building2,
          ariaLabel: 'Companies - Manage employer organizations',
          permission: 'network:manage',
        },
      ],
    },
    {
      title: 'Analytics & Reporting',
      items: [
        {
          label: 'Analytics',
          to: ROUTES.ADMIN_ANALYTICS,
          icon: BarChart,
          ariaLabel: 'Analytics - Platform analytics',
          permission: 'analytics:view',
        },
        {
          label: 'Reports',
          to: ROUTES.ADMIN_REPORTS,
          icon: Download,
          ariaLabel: 'Reports - Download platform reports',
          permission: 'analytics:view',
        },
      ],
    },
    {
      title: 'Training',
      items: [
        {
          label: 'Training',
          to: ROUTES.ADMIN_TRAINING,
          icon: BookMarked,
          ariaLabel: 'Training - Platform documentation and guides',
          permission: 'admin:view',
        },
        {
          label: 'Sitemap Flowcharts',
          to: ROUTES.ADMIN_TRAINING_SITEMAP,
          icon: Map,
          ariaLabel: 'Sitemap Flowcharts - Visual platform navigation maps',
          permission: 'admin:view',
        },
      ],
    },
    {
      title: 'User',
      items: [
        {
          label: 'Settings',
          to: ROUTES.SETTINGS_PROFILE,
          icon: User,
          ariaLabel: 'Settings - Your account settings',
          permission: null, // Always visible
        },
      ],
    },
  ],
};

/**
 * Get navigation config based on user type(s)
 * @param userTypes - Array of user types the current user has
 * @returns Appropriate navigation config for primary user type
 */
export function getNavigationConfig(userTypes: string[]): NavigationConfig {
  // Determine primary user type (priority: platform > employer > learner)
  if (userTypes.includes('platform')) {
    return PLATFORM_NAV;
  }
  if (userTypes.includes('employer')) {
    return EMPLOYER_NAV;
  }
  // Default to learner navigation
  return LEARNER_NAV;
}
